import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  MAX_ITERATIONS,
  MIN_ITERATIONS,
  MAX_ITERATIONS_LIMIT,
} from "../../services/chatTurn";
import type {TurnMessage} from "../../services/conversationHistory";
import {
  ChatThread,
  StoredMessage,
  createThreadId,
  deleteThread as deleteStoredThread,
  deriveThreadTitle,
  loadThreads,
  renameThread as renameStoredThread,
  saveThread,
} from "../../services/chatStore";
import {getWorkbookName} from "../../services/excelContext";
import {getRuntimeConfig} from "../../services/runtimeConfig";
import {
  clearLegacyApiKey,
  getAccessToken,
  getRuntimeAccessError,
  getSignedInUser,
  isNaaSupported,
  signIn as signInAuth,
  signOut as signOutAuth,
} from "../../auth";

export type View = "chat" | "settings" | "threads";
export type AuthStatus =
  | "checking"
  | "authenticated"
  | "sign-in-required"
  | "unsupported";

interface AppState {
  apiKey: string;
  setApiKey: (key: string) => void;
  authStatus: AuthStatus;
  signedInUser: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  agentId: string;
  maxIterations: number;
  setMaxIterations: (value: number) => void;
  view: View;
  setView: (view: View) => void;

  /** File name of the workbook the pane is attached to. */
  workbookName: string;
  /** Every stored thread, most recently active first. */
  threads: ChatThread[];
  activeThreadId: string | null;
  /** The open conversation as the user sees it. */
  messages: StoredMessage[];
  setMessages: (
    update: StoredMessage[] | ((prev: StoredMessage[]) => StoredMessage[]),
  ) => void;
  /**
   * The open conversation as the model remembers it. A ref rather than state
   * because a turn reads it at send time, where a stale closure would silently
   * drop everything said so far.
   */
  historyRef: React.MutableRefObject<TurnMessage[]>;
  /** Writes the open conversation to storage, creating its thread on first use. */
  commitActiveThread: () => void;
  newChat: () => void;
  openThread: (id: string) => void;
  removeThread: (id: string) => void;
  retitleThread: (id: string, title: string) => void;
}

const AppContext = createContext<AppState | null>(null);
export {AppContext};

const STORAGE_KEY_MAX_ITERATIONS = "librechat_max_iterations";

/**
 * Set via the LIBRECHAT_AGENT_ID env var; no per-user agent selection.
 * Injected at runtime by server.js (parity with ADDON_ENV, see App.tsx) so
 * one built image can point at different agents without a rebuild. Falls
 * back to the build-time value (webpack Dotenv) when running `webpack serve`
 * locally.
 */
const AGENT_ID =
  getRuntimeConfig().LIBRECHAT_AGENT_ID ?? process.env.LIBRECHAT_AGENT_ID ?? "";

function clampMaxIterations(value: number): number {
  return Math.min(
    Math.max(Math.trunc(value), MIN_ITERATIONS),
    MAX_ITERATIONS_LIMIT,
  );
}

export function AppProvider({children}: {children: React.ReactNode}) {
  const [apiKey, setApiKeyState] = useState("");
  const [authStatus, setAuthStatus] = useState<AuthStatus>("checking");
  const [signedInUser, setSignedInUser] = useState<string | null>(null);
  const [maxIterations, setMaxIterationsState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_MAX_ITERATIONS);
    const parsed = stored ? Number(stored) : NaN;
    return Number.isFinite(parsed)
      ? clampMaxIterations(parsed)
      : MAX_ITERATIONS;
  });
  const [view, setView] = useState<View>("settings");

  const [workbookName, setWorkbookName] = useState("");
  const [threads, setThreads] = useState<ChatThread[]>(() => loadThreads());
  const [activeThreadId, setActiveThreadIdState] = useState<string | null>(
    null,
  );
  const [messages, setMessagesState] = useState<StoredMessage[]>([]);
  const historyRef = useRef<TurnMessage[]>([]);
  // Mirrors of the two pieces of conversation state that `commitActiveThread`
  // needs to read outside of render, where the state values would be stale.
  const messagesRef = useRef<StoredMessage[]>([]);
  const activeThreadIdRef = useRef<string | null>(null);

  const setMessages = useCallback(
    (
      update: StoredMessage[] | ((prev: StoredMessage[]) => StoredMessage[]),
    ) => {
      const next =
        typeof update === "function" ? update(messagesRef.current) : update;
      messagesRef.current = next;
      setMessagesState(next);
    },
    [],
  );

  const setActiveThreadId = useCallback((id: string | null) => {
    activeThreadIdRef.current = id;
    setActiveThreadIdState(id);
  }, []);

  // Office.js finishes its own bootstrap after mount, so the workbook name is
  // read here rather than during state initialisation.
  useEffect(() => {
    setWorkbookName(getWorkbookName());
  }, []);

  // Reopens the last conversation, so closing the pane — or Excel — is no
  // longer the same thing as throwing the chat away.
  useEffect(() => {
    const [mostRecent] = loadThreads();
    if (!mostRecent) return;
    setActiveThreadId(mostRecent.id);
    setMessages(mostRecent.messages);
    historyRef.current = mostRecent.history;
  }, [setActiveThreadId, setMessages]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapAuth() {
      if (!isNaaSupported()) {
        if (!cancelled) setAuthStatus("unsupported");
        return;
      }

      const missing = getRuntimeAccessError();
      if (missing) {
        if (!cancelled) setAuthStatus("sign-in-required");
        return;
      }

      try {
        const token = await getAccessToken({allowInteractive: false});
        if (cancelled) return;
        setApiKeyState(token);
        clearLegacyApiKey();
        const user = await getSignedInUser();
        setSignedInUser(user);
        setAuthStatus("authenticated");
        setView("chat");
      } catch {
        if (!cancelled) {
          setAuthStatus("sign-in-required");
        }
      }
    }

    void bootstrapAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const setApiKey = useCallback((key: string) => {
    setApiKeyState(key);
    if (!key) {
      clearLegacyApiKey();
    }
  }, []);

  const signIn = useCallback(async () => {
    if (!isNaaSupported()) {
      setAuthStatus("unsupported");
      return;
    }

    try {
      const token = await signInAuth();
      setApiKeyState(token);
      clearLegacyApiKey();
      const user = await getSignedInUser();
      setSignedInUser(user);
      setAuthStatus("authenticated");
      setView("chat");
    } catch {
      setAuthStatus("sign-in-required");
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await signOutAuth();
    } finally {
      setApiKeyState("");
      clearLegacyApiKey();
      setSignedInUser(null);
      setAuthStatus("sign-in-required");
      setView("settings");
    }
  }, []);

  const setMaxIterations = useCallback((value: number) => {
    const clamped = clampMaxIterations(value);
    setMaxIterationsState(clamped);
    localStorage.setItem(STORAGE_KEY_MAX_ITERATIONS, String(clamped));
  }, []);

  const commitActiveThread = useCallback(() => {
    const current = messagesRef.current;
    // A thread is born on its first message, so clicking "New chat" and
    // changing your mind leaves nothing behind in the list.
    const firstUserMessage = current.find((m) => m.role === "user");
    if (!firstUserMessage) return;

    const threadId = activeThreadIdRef.current ?? createThreadId();
    const previous = loadThreads().find((t) => t.id === threadId);

    setThreads(
      saveThread({
        id: threadId,
        title: previous?.title ?? deriveThreadTitle(firstUserMessage.content),
        workbookName: previous?.workbookName ?? getWorkbookName(),
        createdAt: previous?.createdAt ?? Date.now(),
        updatedAt: Date.now(),
        messages: current,
        history: historyRef.current,
      }),
    );

    if (activeThreadIdRef.current !== threadId) setActiveThreadId(threadId);
  }, [setActiveThreadId]);

  const resetConversation = useCallback(() => {
    setActiveThreadId(null);
    setMessages([]);
    historyRef.current = [];
  }, [setActiveThreadId, setMessages]);

  const newChat = useCallback(() => {
    resetConversation();
    setView("chat");
  }, [resetConversation]);

  const openThread = useCallback(
    (id: string) => {
      const thread = loadThreads().find((t) => t.id === id);
      if (!thread) return;
      setActiveThreadId(thread.id);
      setMessages(thread.messages);
      historyRef.current = thread.history;
      setView("chat");
    },
    [setActiveThreadId, setMessages],
  );

  const removeThread = useCallback(
    (id: string) => {
      setThreads(deleteStoredThread(id));
      if (id === activeThreadIdRef.current) resetConversation();
    },
    [resetConversation],
  );

  const retitleThread = useCallback((id: string, title: string) => {
    setThreads(renameStoredThread(id, title));
  }, []);

  return (
    <AppContext.Provider
      value={{
        apiKey,
        setApiKey,
        authStatus,
        signedInUser,
        signIn,
        signOut,
        agentId: AGENT_ID,
        maxIterations,
        setMaxIterations,
        view,
        setView,
        workbookName,
        threads,
        activeThreadId,
        messages,
        setMessages,
        historyRef,
        commitActiveThread,
        newChat,
        openThread,
        removeThread,
        retitleThread,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useAppState(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppState must be used within AppProvider");
  return ctx;
}
